import { z } from 'zod'
import type { RefinementCtx } from 'zod'
import type { CompanyProfile } from '@/types/company'

const optionalTrimmedString = (max?: number, message?: string) =>
  z
    .union([
      z.string().transform((value) => value.trim()),
      z.null(),
      z.undefined(),
    ])
    .superRefine((value, ctx: RefinementCtx) => {
      if (value == null) return
      if (max !== undefined && value.length > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: message ?? `Must be ${max} characters or fewer`,
        })
      }
    })
    .transform((value) => (value == null ? '' : value))

const optionalNumber = (message?: string) =>
  z.preprocess((value) => {
    if (value == null) return null
    if (typeof value === 'string' && value.trim() === '') return null
    if (typeof value === 'number') return value
    if (typeof value === 'string') return Number(value)
    return Number.NaN
  }, z.number().nullable().refine((value) => value === null || Number.isFinite(value), {
    message: message ?? 'Must be a number',
  }))

const optionalDecimalString = (max?: number, maxMessage?: string, numericMessage?: string) =>
  optionalTrimmedString(max, maxMessage).superRefine((value: string, ctx: RefinementCtx) => {
    if (!value) return
    if (!/^\d+(?:\.\d+)?$/.test(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: numericMessage ?? 'Must be a number or decimal',
      })
    }
  })

const requiredString = (
  schema: z.ZodType<string>,
  message: string
) =>
  schema.superRefine((value, ctx: RefinementCtx) => {
    if (!value || value.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
      })
    }
  })

const optionalUrlString = optionalTrimmedString(
  2048,
  'URL must be 2048 characters or fewer'
).superRefine((value: string, ctx: RefinementCtx) => {
  if (!value) return

  try {
    const normalized = /^https?:\/\//i.test(value) ? value : `https://${value}`
    new URL(normalized)
  } catch {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Enter a valid URL',
    })
  }
})

const requiredUrlString = requiredString(optionalUrlString, 'Website is required')

const optionalEmailString = optionalTrimmedString(
  255,
  'Company email must be 255 characters or fewer'
).superRefine((value: string, ctx: RefinementCtx) => {
  if (!value) return
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Enter a valid company email',
    })
  }
})

const personSchema = z.object({
  first_name: optionalTrimmedString(120, 'First name must be 120 characters or fewer'),
  last_name: optionalTrimmedString(120, 'Last name must be 120 characters or fewer'),
  role: optionalTrimmedString(120, 'Role must be 120 characters or fewer'),
  role_start_date: optionalTrimmedString(32, 'Role start date must be 32 characters or fewer'),
})

const stringArraySchema = z
  .union([z.array(z.string().trim().max(120)), z.null(), z.undefined()])
  .transform((value) =>
    value
      ?.map((item) => item.trim())
      .filter((item) => item !== '') ?? []
  )

const projectSchema = z.object({
  name: optionalTrimmedString(200, 'Project name must be 200 characters or fewer'),
  stage: optionalTrimmedString(120, 'Stage must be 120 characters or fewer'),
  description: optionalTrimmedString(5000, 'Project description must be 5000 characters or fewer'),
  location_label: optionalTrimmedString(255, 'Location must be 255 characters or fewer'),
  location_lat: optionalNumber('Latitude must be a number'),
  location_lng: optionalNumber('Longitude must be a number'),
  commodities: stringArraySchema,
  activity_feed: z
    .union([
      z.array(z.object({
        date: optionalTrimmedString(32, 'Activity date must be 32 characters or fewer'),
        text: optionalTrimmedString(1000, 'Activity update must be 1000 characters or fewer'),
      })),
      z.null(),
      z.undefined(),
    ])
    .transform((value) =>
      value
        ?.map((item) => ({
          date: item.date ?? '',
          text: item.text ?? '',
        }))
        .filter((item) => item.date !== '' || item.text !== '') ?? []
    ),
})

const jobLinkSchema = z.object({
  type: z.enum(['linkedin', 'seek', 'other']),
  title: optionalTrimmedString(120, 'Title must be 120 characters or fewer'),
  description: optionalTrimmedString(255, 'Description must be 255 characters or fewer'),
  url: optionalUrlString,
})

const socialLinkSchema = z.object({
  type: z.enum(['linkedin', 'facebook', 'instagram', 'x', 'youtube']),
  url: optionalUrlString,
})

const keyHireSchema = z.object({
  name: optionalTrimmedString(160, 'Name must be 160 characters or fewer'),
  role: optionalTrimmedString(160, 'Role must be 160 characters or fewer'),
  date: optionalTrimmedString(32, 'Date must be 32 characters or fewer'),
  background: optionalTrimmedString(255, 'Background must be 255 characters or fewer'),
  summary: optionalTrimmedString(255, 'Summary must be 255 characters or fewer'),
  url: optionalUrlString,
})

const capitalRaisingSchema = z.object({
  date: optionalTrimmedString(32, 'Date must be 32 characters or fewer'),
  amount: optionalTrimmedString(64, 'Amount must be 64 characters or fewer'),
  label: optionalTrimmedString(120, 'Type must be 120 characters or fewer'),
  summary: optionalTrimmedString(255, 'Summary must be 255 characters or fewer'),
  url: optionalUrlString,
})

const benefitSchema = z.object({
  heading: optionalTrimmedString(120, 'Heading must be 120 characters or fewer'),
  description: optionalTrimmedString(255, 'Description must be 255 characters or fewer'),
  source: optionalTrimmedString(120, 'Source must be 120 characters or fewer'),
  added_at: optionalTrimmedString(32, 'Added date must be 32 characters or fewer'),
})

const jobLinksArraySchema = z
  .union([z.array(jobLinkSchema), z.null(), z.undefined()])
  .transform((value) =>
    value?.filter((entry) => (entry.url ?? '').trim() !== '') ?? []
  )

const socialLinksArraySchema = z
  .union([z.array(socialLinkSchema), z.null(), z.undefined()])
  .transform((value) =>
    value?.filter((entry) => (entry.url ?? '').trim() !== '') ?? []
  )

const peopleArraySchema = z
  .union([z.array(personSchema), z.null(), z.undefined()])
  .transform((value) => value ?? [])

const requiredPeopleArraySchema = peopleArraySchema.superRefine((people, ctx: RefinementCtx) => {
  if (!Array.isArray(people) || people.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Add at least one key person',
      path: ['key_people'],
    })
    return
  }

  const hasCompleteRecord = people.some(
    (person) =>
      (person.first_name ?? '').trim() &&
      (person.last_name ?? '').trim() &&
      (person.role ?? '').trim()
  )

  if (!hasCompleteRecord) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Add at least one key person with first name, last name, and role',
      path: ['key_people'],
    })
  }
})

const industryAttributesSchema = z
  .union([
    z.record(z.string(), z.union([z.string(), z.array(z.string())])),
    z.array(z.unknown()),
    z.null(),
    z.undefined(),
  ])
  // The API encodes an empty PHP array as [], so normalise anything that is not an object.
  .transform((value) => (value && !Array.isArray(value) ? value : {}))

const projectsArraySchema = z
  .union([z.array(projectSchema), z.null(), z.undefined()])
  .transform((value) => value ?? [])

const keyHiresArraySchema = z
  .union([z.array(keyHireSchema), z.null(), z.undefined()])
  .transform((value) => value ?? [])

const capitalRaisingsArraySchema = z
  .union([z.array(capitalRaisingSchema), z.null(), z.undefined()])
  .transform((value) => value ?? [])

const benefitsArraySchema = z
  .union([z.array(benefitSchema), z.null(), z.undefined()])
  .transform((value) => value ?? [])

const requiredTrimmedString = (
  max: number | undefined,
  maxMessage: string | undefined,
  requiredMessage: string
) => requiredString(optionalTrimmedString(max, maxMessage), requiredMessage)

const isMiningIndustryValue = (industry: unknown): boolean => {
  if (industry == null) return false

  if (typeof industry === 'object') {
    if ('name' in (industry as Record<string, unknown>)) {
      return isMiningIndustryValue((industry as Record<string, unknown>).name)
    }
    if ('label' in (industry as Record<string, unknown>)) {
      return isMiningIndustryValue((industry as Record<string, unknown>).label)
    }
  }

  if (typeof industry === 'string' || typeof industry === 'number') {
    const normalized = String(industry).trim().toLowerCase()
    if (!normalized) return false
    return normalized.includes('mining')
  }

  return false
}

export const companyProfileSchema = z
  .object({
    id: z.number().optional(),
    name: z
      .string()
      .trim()
      .min(1, { message: 'Company name is required' })
      .max(255, { message: 'Company name must be 255 characters or fewer' }),
    slug: optionalTrimmedString(255, 'Slug must be 255 characters or fewer'),
    logo: optionalTrimmedString(2048, 'Logo URL must be 2048 characters or fewer'),
    logo_type: optionalTrimmedString(255, 'Logo type must be 255 characters or fewer'),
    website: requiredUrlString,
    contact_email: optionalEmailString,
    office_address: requiredTrimmedString(
      255,
      'Address must be 255 characters or fewer',
      'Head office is required'
    ),
    employee_size: requiredTrimmedString(
      120,
      'Employee size must be 120 characters or fewer',
      'Employee size is required'
    ),
    headcount_growth_12_months: optionalTrimmedString(
      64,
      'Headcount growth must be 64 characters or fewer'
    ),
    average_employee_tenure: optionalTrimmedString(
      64,
      'Average employee tenure must be 64 characters or fewer'
    ),
    industry: requiredTrimmedString(
      120,
      'Industry value must be 120 characters or fewer',
      'Industry is required'
    ),
    industry_subcategory: optionalTrimmedString(120, 'Subcategory must be 120 characters or fewer'),
    industry_subcategory_id: z.union([z.number(), z.string(), z.null(), z.undefined()]).optional(),
    industry_attributes: industryAttributesSchema,
    summary: requiredTrimmedString(
      4000,
      'Summary must be 4000 characters or fewer',
      'Company summary is required'
    ),
    commodities: stringArraySchema,
    key_people: requiredPeopleArraySchema,
    key_projects: projectsArraySchema,
    recent_key_hires: keyHiresArraySchema,
    job_links: jobLinksArraySchema,
    social_links: socialLinksArraySchema,
    ticker: optionalTrimmedString(12, 'Ticker must be 12 characters or fewer'),
    market_cap: optionalTrimmedString(64, 'Market cap must be 64 characters or fewer'),
    is_private_company: z.boolean().optional(),
    cash_in_bank: optionalTrimmedString(64, 'Cash in bank must be 64 characters or fewer'),
    cash_in_bank_updated_at: optionalTrimmedString(32, 'Date of balance must be 32 characters or fewer'),
    cash_runway: optionalDecimalString(
      64,
      'Cash runway must be 64 characters or fewer',
      'Cash runway must be a number or decimal'
    ),
    recent_capital_raisings: capitalRaisingsArraySchema,
    benefits: benefitsArraySchema,
    field_roster: optionalTrimmedString(255, 'Field roster must be 255 characters or fewer'),
    typical_hours: optionalTrimmedString(255, 'Typical hours must be 255 characters or fewer'),
    facilities: optionalTrimmedString(4000, 'Facilities must be 4000 characters or fewer'),
    software_used: stringArraySchema,
    creation_date: optionalTrimmedString(64, 'Creation date must be 64 characters or fewer'),
    is_draft: z.boolean().optional(),
  })
  .superRefine((values, ctx) => {
    const hasTicker = (values.ticker ?? '').trim() !== ''
    const hasMarketCap = (values.market_cap ?? '').trim() !== ''
    const mining = isMiningIndustryValue(values.industry)

    // if (mining && !hasTicker && !hasMarketCap) {
    //   ctx.addIssue({
    //     code: z.ZodIssueCode.custom,
    //     path: ['market_cap'],
    //     message: 'Enter a market cap or provide an exchange ticker',
    //   })
    // }

    if (mining) {
      // Company-level commodities are optional even for mining companies.
    }

    if ((values.cash_in_bank ?? '').trim() && !(values.cash_in_bank_updated_at ?? '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cash_in_bank_updated_at'],
        message: 'Date of Balance is required when cash in bank is provided',
      })
    }
  })

export type CompanyProfileFormValues = z.infer<typeof companyProfileSchema>

const EMPTY_TO_NULL_FIELDS: Array<keyof CompanyProfileFormValues> = [
  'logo',
  'logo_type',
  'website',
  'contact_email',
  'office_address',
  'employee_size',
  'headcount_growth_12_months',
  'average_employee_tenure',
  'industry',
  'industry_subcategory',
  'summary',
  'ticker',
  'market_cap',
  'cash_in_bank',
  'cash_in_bank_updated_at',
  'cash_runway',
  'field_roster',
  'typical_hours',
  'facilities',
  'creation_date',
]

export const prepareCompanyProfilePayload = (
  values: CompanyProfileFormValues
): Partial<CompanyProfile> => {
  const payload: Record<string, unknown> = { ...values }

  EMPTY_TO_NULL_FIELDS.forEach((field) => {
    const value = payload[field]
    if (typeof value === 'string' && value.trim() === '') {
      payload[field] = null
    }
  })

  if (Array.isArray(values.key_people)) {
    payload.key_people = values.key_people
      .map((person) => ({
        first_name: person.first_name ?? '',
        last_name: person.last_name ?? '',
        role: person.role ?? '',
        role_start_date: person.role_start_date ?? '',
      }))
      .filter((person) =>
        person.first_name !== '' ||
        person.last_name !== '' ||
        person.role !== '' ||
        person.role_start_date !== ''
      )
  }

  if (Array.isArray(values.key_projects)) {
    payload.key_projects = values.key_projects
      .map((project) => ({
        name: project.name ?? '',
        stage: project.stage ?? '',
        description: project.description ?? '',
        location_label: project.location_label ?? '',
        location_lat: project.location_lat ?? null,
        location_lng: project.location_lng ?? null,
        commodities: Array.isArray(project.commodities) ? project.commodities : [],
        activity_feed: Array.isArray(project.activity_feed)
          ? project.activity_feed.map((entry) => ({
              date: entry.date ?? '',
              text: entry.text ?? '',
            })).filter((entry) => entry.date !== '' || entry.text !== '')
          : [],
      }))
      .filter((project) => {
        const hasText =
          project.name !== '' ||
          project.stage !== '' ||
          project.description !== '' ||
          project.location_label !== ''
        const hasCoords =
          typeof project.location_lat === 'number' ||
          typeof project.location_lng === 'number'
        const hasCommodities = Array.isArray(project.commodities) && project.commodities.length > 0
        const hasActivity = Array.isArray(project.activity_feed) && project.activity_feed.length > 0
        return hasText || hasCoords || hasCommodities || hasActivity
      })
  }

  if (Array.isArray(values.job_links)) {
    payload.job_links = values.job_links
      .map((jobLink) => ({
        type: jobLink.type,
        title: jobLink.title ?? '',
        description: jobLink.description ?? '',
        url: jobLink.url ?? '',
      }))
      .filter((jobLink) => jobLink.url.trim() !== '')
  }

  if (Array.isArray(values.social_links)) {
    payload.social_links = values.social_links
      .map((socialLink) => ({
        type: socialLink.type,
        url: socialLink.url ?? '',
      }))
      .filter((socialLink) => socialLink.url.trim() !== '')
  }

  if (Array.isArray(values.recent_key_hires)) {
    payload.recent_key_hires = values.recent_key_hires
      .map((hire) => ({
        name: hire.name ?? '',
        role: hire.role ?? '',
        date: hire.date ?? '',
        background: hire.background ?? '',
        summary: hire.summary ?? '',
        url: hire.url ?? '',
      }))
      .filter((hire) =>
        hire.name !== '' ||
        hire.role !== '' ||
        hire.date !== '' ||
        hire.background !== '' ||
        hire.summary !== '' ||
        hire.url.trim() !== ''
      )
  }

  if (Array.isArray(values.recent_capital_raisings)) {
    payload.recent_capital_raisings = values.recent_capital_raisings
      .map((raising) => ({
        date: raising.date ?? '',
        amount: raising.amount ?? '',
        label: raising.label ?? '',
        summary: raising.summary ?? '',
        url: raising.url ?? '',
      }))
      .filter((raising) =>
        raising.date !== '' ||
        raising.amount !== '' ||
        raising.label !== '' ||
        raising.summary !== '' ||
        raising.url.trim() !== ''
      )
  }

  if (Array.isArray(values.benefits)) {
    payload.benefits = values.benefits
      .map((benefit) => ({
        heading: benefit.heading ?? '',
        description: benefit.description ?? '',
        source: benefit.source ?? '',
        added_at: benefit.added_at ?? '',
      }))
      .filter((benefit) =>
        benefit.heading !== '' ||
        benefit.description !== '' ||
        benefit.source !== ''
      )
  }

  if (payload.industry_subcategory_id === '' || payload.industry_subcategory_id == null) {
    payload.industry_subcategory_id = null
  } else {
    const parsedSubcategoryId = Number(payload.industry_subcategory_id)
    payload.industry_subcategory_id = Number.isFinite(parsedSubcategoryId) && parsedSubcategoryId > 0
      ? parsedSubcategoryId
      : null
  }

  delete payload.slug

  return payload as Partial<CompanyProfile>
}

export const mapZodErrors = (error: z.ZodError): Record<string, string> => {
  const fieldErrors: Record<string, string> = {}

  for (const issue of error.issues) {
    const path = issue.path.join('.') || 'form'
    if (!fieldErrors[path]) {
      fieldErrors[path] = issue.message
    }
  }

  return fieldErrors
}
